import { useEffect, useState } from "react";
import api, { fmtMXN, fmtMXNFull, fmtDate } from "@/lib/api";
import { X, FileDown, Loader2 } from "lucide-react";

const STATUS = { borrador: ["PENDIENTE", "#fbbf24"], pagado: ["PAGADO", "var(--green)"], cancelado: ["CANCELADO", "var(--red)"] };

export default function EmployeeStatementModal({ employee, onClose }) {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/employees/${employee.id}/statement`).then(({ data }) => setData(data)).catch(() => {});
  }, [employee.id]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/employees/${employee.id}/statement/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estado_cuenta_${employee.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="panel-strong w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="employee-statement-modal">
        <div className="panel-header px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <div className="label-mono">// ESTADO DE CUENTA · NÓMINA</div>
            <h2 className="font-display font-bold text-xl text-slate-100">{employee.name}</h2>
            <div className="text-[11px] text-slate-400">{employee.position} · Salario {fmtMXN(employee.base_salary)} <span className="capitalize">({employee.payment_period})</span></div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="statement-pdf-btn" onClick={downloadPdf} disabled={downloading}
              className="btn-primary px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {!data ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
        ) : (
          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi label="TOTAL ENTREGADO" value={fmtMXNFull(data.total_pagado)} color="var(--green)" testId="statement-total-paid" />
              <Kpi label="PENDIENTE" value={fmtMXNFull(data.total_borrador)} color="#fbbf24" />
              <Kpi label="PAGOS" value={data.pagos} color="var(--cyan)" />
              <Kpi label="FIRMADOS" value={data.firmados} color="#a78bfa" />
            </div>
            <div className="panel overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[560px]" data-testid="statement-table">
                  <thead className="panel-header">
                    <tr className="text-left">
                      <th className="label-mono px-3 py-2.5">Período</th>
                      <th className="label-mono px-3 py-2.5 text-right">Neto</th>
                      <th className="label-mono px-3 py-2.5">Estado</th>
                      <th className="label-mono px-3 py-2.5">Pagado el</th>
                      <th className="label-mono px-3 py-2.5">Firmado</th>
                      <th className="label-mono px-3 py-2.5 text-right">Recibo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payrolls.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-slate-500">Sin pagos registrados.</td></tr>
                    )}
                    {data.payrolls.map((p) => {
                      const [lbl, color] = STATUS[p.status] || [p.status, "var(--text-dim)"];
                      return (
                        <tr key={p.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                          <td className="px-3 py-2 text-slate-300">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: "var(--cyan)" }}>{fmtMXNFull(p.net)}</td>
                          <td className="px-3 py-2"><span className="font-bold text-[10px]" style={{ color }}>{lbl}</span></td>
                          <td className="px-3 py-2 text-slate-400">{p.paid_date ? fmtDate(p.paid_date) : (p.paid_at ? fmtDate(p.paid_at.slice(0, 10)) : "—")}</td>
                          <td className="px-3 py-2">{p.signed_at ? <span style={{ color: "var(--green)" }}>✓ SÍ</span> : <span className="text-slate-500">—</span>}</td>
                          <td className="px-3 py-2 text-right">
                            <a href={`/payroll/${p.id}/receipt`} target="_blank" rel="noreferrer" className="text-[10px] font-semibold hover:underline" style={{ color: "var(--cyan)" }}>VER</a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Kpi = ({ label, value, color, testId }) => (
  <div className="px-3 py-2.5 rounded-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }} data-testid={testId}>
    <div className="label-mono text-[8px]">{label}</div>
    <div className="font-mono font-bold text-base mt-0.5" style={{ color }}>{value}</div>
  </div>
);
